// Import react elements
import { useContext, useEffect, useState } from "react";

// Import strich api calls
import {
    requestPayPalBillingLink,
    requestPayPalBillingUnlink,
    getUserAccountData,
} from "api/APIStrichCalls";

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import hooks
import { useToast } from "global/hooks/useToast";

// Import components
import CreateButton from "global/components/CreateButton";



const LinkPayPalBilling = ({ userData, setUserData }) => {

    // Get the auth token
    const { token } = useContext(AuthContext);

    // Get the toast hook
    const { toastError, toastSuccess } = useToast();

    // Set the linked status
    const [billingIsLinked, setBillingIsLinked] = useState(userData?.paypal_billing_link_date || "");

    // Store the current Stripe billing status label
    const [statusText, setStatusText] = useState(userData?.paypal_billing_link_date ? "Linked" : "Not linked");



    // Update the linked status when the user data changes
    useEffect(() => {

        setBillingIsLinked(userData?.paypal_billing_link_date || "");

        // Update the status text from props
        if (userData?.paypal_billing_link_date) {

            // Mark linked when a link date exists
            setStatusText("Linked");

        } else {

            // Otherwise mark as not linked
            setStatusText("Not linked");

        }

        
    }, [userData?.email, userData?.paypal_billing_link_date]);



    // Listen for the PayPal success tab completing the billing link
    useEffect(() => {

        // Create the channel shared with the PayPal success page
        const paypalBillingChannel = new BroadcastChannel("paypal-billing");

        // Refresh the account page after PayPal billing has been linked
        paypalBillingChannel.onmessage = (event) => {

            // Ignore unrelated messages
            if (event.data !== "linked") {

                return;

            }

            // Reload the account so the newly linked billing details are shown
            window.location.reload();

        };

        // Close the channel when this component unmounts
        return () => {

            paypalBillingChannel.close();

        };

    }, []);



    // Handle linking PayPal billing
    const handleLinkPayPalBilling = async () => {

        // Make the API call
        const response = await requestPayPalBillingLink(token);

        // Check if the call failed
        if (!response?.success) {

            toastError({
                title: "Error",
                message: response?.error_message || "Oops...something went wrong.",
                autoCloseMs: 6000
            });

            return;

        }

        // Check if the action url was returned
        if (!response?.results?.action_url) {

            toastError({
                title: "Error",
                message: "No PayPal action URL returned.",
                autoCloseMs: 6000
            });

            return;

        }

        // Open the PayPal window
        const paypalWindow = window.open(response.results.action_url, "_blank", "noopener,noreferrer");

        // Check if the popup was blocked
        if (!paypalWindow) {

            toastError({
                title: "Error",
                message: "Popup blocked. Please allow popups and try again.",
                autoCloseMs: 6000
            });

        }

    };



    // Handle unlinking PayPal billing
    const handleUnlinkPayPalBilling = async () => {

        // Make the API call
        const response = await requestPayPalBillingUnlink(token);

        // Check if the call failed
        if (!response?.success) {

            toastError({
                title: "Error",
                message: response?.error_message || "Oops...something went wrong.",
                autoCloseMs: 6000
            });

            return;

        }

        // Update the local linked status immediately
        setBillingIsLinked(false);

        // Update the local status label immediately
        setStatusText("Not linked");

        // Refresh the account data so PayPal moves out of the active billing list
        await getUserAccountData(token, setUserData);

        // Toast success
        toastSuccess({ title: "Success", message: "PayPal billing link removed." });

    };



    return (

        <div className="billing-method-container">
           

            {/* This is the heading area */}
            <div className="stripe-billing-manager__header">

                {/* This is the heading */}
                <div className={`horizontal-align-content-center account-billing-preference__header ${billingIsLinked ? "account-billing-preference__header--active" : "account-billing-preference__header--inactive"}`}><h3>Paypal</h3></div>

                {/* This is the status sub heading */}
                <p className="stripe-billing-manager__status">
                    <span className={`stripe-billing-manager__status-badge stripe-billing-manager__status-badge--${billingIsLinked ? "linked" : "unlinked"}`}>
                        {statusText}
                    </span>
                </p>

            </div>

            

            {/* Show wording that matches the current PayPal billing status */}
            {billingIsLinked ?

                <p>PayPal linked to allow automatic billing for fulfilments and charges.</p>

                :

                <p>Link PayPal to allow automatic billing for fulfilments and charges.</p>

            }
            
            <br />

            {billingIsLinked ? 

                <CreateButton
                    title="Unlink Paypal"
                    size="s"
                    invert={true}
                    onClick={handleUnlinkPayPalBilling}
                />

                :

                <CreateButton
                    title="Link PayPal"
                    size="s"
                    onClick={handleLinkPayPalBilling}
                />
                          
            }

        </div>

    );

};

export default LinkPayPalBilling;